import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiGet } from '../lib/api';

/**
 * TrackingScriptLoader - Dynamically loads tracking scripts from backend
 * 
 * Features:
 * - Loads active scripts based on current page path and scope
 * - Injects scripts into HEAD, BODY_START, or BODY_END
 * - Prevents duplicate injections
 * - Wraps execution safely to prevent breaking the page
 * - SSR-safe (only runs on client)
 */
const TrackingScriptLoader = ({ pageType = null }) => {
  const location = useLocation();
  const [scripts, setScripts] = useState({ HEAD: [], BODY_START: [], BODY_END: [] });
  const injectedScriptsRef = useRef(new Set());
  const bodyStartContainerRef = useRef(null);
  const bodyEndContainerRef = useRef(null);

  // Check if current page is admin-related - don't track admin pages
  const isAdminPage = location.pathname.startsWith('/admin') || 
                      location.pathname.includes('login-goturkeytransfer') ||
                      location.pathname.includes('/login');

  // Fetch active scripts from backend (skip for admin pages)
  useEffect(() => {
    // Don't load tracking scripts on admin pages
    if (isAdminPage) {
      setScripts({ HEAD: [], BODY_START: [], BODY_END: [] });
      return;
    }

    const fetchScripts = async () => {
      const params = new URLSearchParams();
      params.append('page_path', location.pathname);
      if (pageType) {
        params.append('page_type', pageType);
      }
      
      const data = await apiGet(`/api/tracking/active?${params}`);
      
      if (data && data.success && data.scripts) {
        setScripts(data.scripts);
      }
    };

    fetchScripts();
  }, [location.pathname, pageType, isAdminPage]);

  // Inject scripts into HEAD
  useEffect(() => {
    if (!scripts.HEAD || scripts.HEAD.length === 0) return;

    scripts.HEAD.forEach((script) => {
      const scriptId = `tracking-head-${script.id}`;
      
      // Prevent duplicate injection
      if (injectedScriptsRef.current.has(scriptId)) return;
      if (document.getElementById(scriptId)) return;
      
      try {
        // Create a container div to parse the HTML
        const container = document.createElement('div');
        container.innerHTML = script.script_code;
        
        // Extract and inject all script and noscript elements
        const elements = container.querySelectorAll('script, noscript, link, meta');
        
        elements.forEach((element, index) => {
          const clone = element.cloneNode(true);
          clone.id = `${scriptId}-${index}`;
          clone.setAttribute('data-tracking-script', script.name);
          
          if (element.tagName === 'SCRIPT') {
            // For script tags, we need to recreate them to execute
            const newScript = document.createElement('script');
            
            // Copy attributes
            Array.from(clone.attributes).forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });
            
            // Copy inline content
            if (clone.innerHTML) {
              newScript.innerHTML = clone.innerHTML;
            }
            
            document.head.appendChild(newScript);
          } else {
            document.head.appendChild(clone);
          }
        });
        
        // If no script/noscript elements found, inject raw content
        if (elements.length === 0 && script.script_code.trim()) {
          const wrapper = document.createElement('div');
          wrapper.id = scriptId;
          wrapper.setAttribute('data-tracking-script', script.name);
          wrapper.innerHTML = script.script_code;
          
          // Move scripts from wrapper to head
          const scriptElements = wrapper.getElementsByTagName('script');
          Array.from(scriptElements).forEach((el) => {
            const newScript = document.createElement('script');
            Array.from(el.attributes).forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });
            if (el.innerHTML) {
              newScript.innerHTML = el.innerHTML;
            }
            document.head.appendChild(newScript);
          });
        }
        
        injectedScriptsRef.current.add(scriptId);
      } catch (error) {
        console.error(`Failed to inject tracking script "${script.name}":`, error);
      }
    });
  }, [scripts.HEAD]);

  // Create BODY_START container if needed
  useEffect(() => {
    if (!scripts.BODY_START || scripts.BODY_START.length === 0) return;
    
    // Find or create container at start of body
    let container = document.getElementById('tracking-body-start');
    if (!container) {
      container = document.createElement('div');
      container.id = 'tracking-body-start';
      container.style.display = 'none';
      
      // Insert at the beginning of body
      if (document.body.firstChild) {
        document.body.insertBefore(container, document.body.firstChild);
      } else {
        document.body.appendChild(container);
      }
    }
    bodyStartContainerRef.current = container;

    scripts.BODY_START.forEach((script) => {
      const scriptId = `tracking-body-start-${script.id}`;
      
      if (injectedScriptsRef.current.has(scriptId)) return;
      if (document.getElementById(scriptId)) return;
      
      try {
        const wrapper = document.createElement('div');
        wrapper.id = scriptId;
        wrapper.setAttribute('data-tracking-script', script.name);
        wrapper.innerHTML = script.script_code;
        
        // Execute inline scripts
        const scriptElements = wrapper.getElementsByTagName('script');
        Array.from(scriptElements).forEach((el) => {
          const newScript = document.createElement('script');
          Array.from(el.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          if (el.innerHTML) {
            newScript.innerHTML = el.innerHTML;
          }
          container.appendChild(newScript);
        });
        
        // Append noscript elements
        const noscriptElements = wrapper.getElementsByTagName('noscript');
        Array.from(noscriptElements).forEach((el) => {
          container.appendChild(el.cloneNode(true));
        });
        
        injectedScriptsRef.current.add(scriptId);
      } catch (error) {
        console.error(`Failed to inject BODY_START script "${script.name}":`, error);
      }
    });
  }, [scripts.BODY_START]);

  // Create BODY_END container if needed
  useEffect(() => {
    if (!scripts.BODY_END || scripts.BODY_END.length === 0) return;
    
    // Find or create container at end of body
    let container = document.getElementById('tracking-body-end');
    if (!container) {
      container = document.createElement('div');
      container.id = 'tracking-body-end';
      container.style.display = 'none';
      document.body.appendChild(container);
    }
    bodyEndContainerRef.current = container;

    scripts.BODY_END.forEach((script) => {
      const scriptId = `tracking-body-end-${script.id}`;
      
      if (injectedScriptsRef.current.has(scriptId)) return;
      if (document.getElementById(scriptId)) return;
      
      try {
        const wrapper = document.createElement('div');
        wrapper.id = scriptId;
        wrapper.setAttribute('data-tracking-script', script.name);
        wrapper.innerHTML = script.script_code;
        
        // Execute inline scripts
        const scriptElements = wrapper.getElementsByTagName('script');
        Array.from(scriptElements).forEach((el) => {
          const newScript = document.createElement('script');
          Array.from(el.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          if (el.innerHTML) {
            newScript.innerHTML = el.innerHTML;
          }
          container.appendChild(newScript);
        });
        
        injectedScriptsRef.current.add(scriptId);
      } catch (error) {
        console.error(`Failed to inject BODY_END script "${script.name}":`, error);
      }
    });
  }, [scripts.BODY_END]);

  // Cleanup on unmount is not done because tracking scripts
  // should persist across page navigations in SPA

  // This component doesn't render anything visible
  return null;
};

export default TrackingScriptLoader;
